import { useEffect, useState } from "react";
import { Route, useHistory } from "react-router-dom";

import {
  isTokenExpired,
  isUserWfpManager,
  redirectToCorrectLocation,
  setupClient,
  setupCountry,
  isUserExternal,
} from "../../utils/authentication";
import { WfpManagerHeader } from "../WfpManagerHeader";
import RepresentativeTermsGate from "../../components/RepresentativeTermsGate";
import { FullscreenLoading } from "../../components/FullscreenLoading";
import { ensureSelectedCountry } from "../../utils/countryStorage";
import { WfpManagerRouteProps } from "./types";

type AuthStatus = "loading" | "loggedIn" | "loggedOut";

// Route guard for /wfpManager/* paths, open to both WFP managers and external
// users. Auth is evaluated once on mount, mirroring the original class guard.
export const WfpManagerRoute = ({
  component: Component,
  path,
  exact,
  fullScreen = false,
  disallowExternalUser = false,
  externalUserRedirectPath = "/wfpManager",
}: WfpManagerRouteProps) => {
  const history = useHistory();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [isExternalUser, setIsExternalUser] = useState(false);

  useEffect(() => {
    const ciamAccessToken = localStorage.getItem("ciam_access_token");
    const userProfile = localStorage.getItem("user_profile");
    const selectedCountry = ensureSelectedCountry();
    const expiryDate = new Date(localStorage.getItem("expiry_date") ?? 0);

    if (!ciamAccessToken || !userProfile || isTokenExpired(expiryDate)) {
      setStatus("loggedOut");
      history.push("/login");
    } else if (!(isUserWfpManager(userProfile) || isUserExternal(userProfile))) {
      redirectToCorrectLocation(userProfile, history);
    } else {
      setupClient(ciamAccessToken);
      if (selectedCountry) {
        setupCountry(selectedCountry);
      }
      const external = isUserExternal(userProfile);
      if (external && disallowExternalUser) {
        history.replace(externalUserRedirectPath);
        return;
      }
      setIsExternalUser(external);
      setStatus("loggedIn");
    }
  }, [history, disallowExternalUser, externalUserRedirectPath]);

  if (status === "loading") {
    return <FullscreenLoading isVisible />;
  }

  if (status === "loggedIn") {
    // External users share these routes but must accept the latest Terms of Use
    // first; WFP managers have no terms fields so they skip the gate.
    const termsGate = isExternalUser ? <RepresentativeTermsGate /> : null;
    const route = <Route key={path} exact={exact} path={path} component={Component} />;

    if (fullScreen) {
      return (
        <>
          {termsGate}
          {route}
        </>
      );
    }

    return (
      <div>
        {termsGate}
        <WfpManagerHeader>{route}</WfpManagerHeader>
      </div>
    );
  }

  return <FullscreenLoading isVisible />;
};
